/**
 * Optional native input and autofill alongside the conversation. Values remain
 * in /form/{id}. A visitor edit attaches the declared fields to the main composer,
 * where they can send them with an additional message. There is no form-owned submit.
 */
import { useContext, type FC } from 'react';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { SurfaceHeader } from '@/app/lib/a2ui/blocks/SurfaceHeader.tsx';
import { SmartFormFields, type SmartField } from '@/app/lib/a2ui/blocks/SmartFormFields.tsx';
import { stageFieldDraft } from '../field-draft.ts';
import { BlockSkeleton } from '@/app/lib/a2ui/blocks/BlockSkeleton.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { useIntl } from 'react-intl';
import { messages } from '@/app/lib/localization/messages.ts';
import { readFormColumns, readFormOptions } from './form-options.ts';

const FIELD_KINDS: ReadonlySet<string> = new Set(['text', 'email', 'phone', 'choice', 'note']);

function isFieldKind(value: string): value is SmartField['kind'] {
  return FIELD_KINDS.has(value);
}

interface FormField {
  id: string;
  label: string;
  kind: SmartField['kind'];
  options?: SmartField['options'];
  value?: string;
  prefilled?: boolean;
  errors?: string[];
  autoComplete?: string;
}

function readErrors(raw: unknown): string[] | undefined {
  const values = arr(raw).filter((error): error is string => typeof error === 'string');
  return values.length > 0 ? values : undefined;
}

function readField(raw: unknown): FormField | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = str(raw.id);
  const label = str(raw.label);
  const kind = str(raw.kind);
  if (!id || !label || !isFieldKind(kind)) {
    return null;
  }
  return {
    id,
    label,
    kind,
    options: readFormOptions(raw.options),
    value: optStr(raw.value),
    prefilled: raw.prefilled === true,
    errors: readErrors(raw.errors),
    autoComplete: optStr(raw.autoComplete),
  };
}

function fieldPointer(fieldId: string): string {
  return `/form/${fieldId}`;
}

export const FormSurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const intl = useIntl();
  const fields = arr(node.props.fields)
    .map(readField)
    .filter((field): field is FormField => field !== null);

  if (!Array.isArray(node.props.fields) || fields.length === 0) {
    return <BlockSkeleton variant="form" />;
  }

  const handleChange = (id: string, value: string) => {
    api?.setValue(fieldPointer(id), value);
    stageFieldDraft(api, {
      id: node.id,
      label: str(node.props.draftLabel) || intl.formatMessage(messages.enteredDetails),
      instruction: intl.formatMessage(messages.useDetails),
      context: { component: node.component, nodeId: node.id },
      fields: fields.map((field) => ({ ...field, path: fieldPointer(field.id) })),
    });
  };

  const smartFields: SmartField[] = fields.map((field) => {
    const saved = api?.getValue(fieldPointer(field.id));
    return {
      id: field.id,
      label: field.label,
      kind: field.kind,
      options: field.options,
      value: saved === undefined ? (field.value ?? '') : str(saved),
      prefilled: field.prefilled,
      errors: field.errors,
      autoComplete: field.autoComplete,
    };
  });

  const title = str(node.props.highlight ?? node.props.title);
  const subtitle = optStr(node.props.message ?? node.props.subtitle);

  return (
    <div className="w-full min-w-0 max-w-container-form">
      <SurfaceHeader title={title} subtitle={subtitle} />
      <p className="mb-4 text-sm text-muted-foreground">
        {intl.formatMessage(messages.inputAlternative)}
      </p>
      <SmartFormFields
        fields={smartFields}
        disabled={!api?.addToMessage}
        onChange={handleChange}
        onCommit={() => api?.commitValue()}
        columns={readFormColumns(node.props.columns)}
        prefilledLabel={intl.formatMessage(messages.prefilled)}
      />
    </div>
  );
};
